/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Input from "../common/Input";
import SelectInput from "../common/SelectInput";
import Checkbox from "../common/Checkbox";
import { STATUS } from "../common/Const";
import { timeStringToMinutes, minutesToTimeString } from "../common/Helpers";

function CreateClassModal({
  isOpen,
  initialDate,
  initialTime,
  initialDayOfWeek,
  initialTeacherId,
  classTypes,
  teachers,
  books,
  weekdays,
  onClose,
  onSave,
}) {
  const [formData, setFormData] = useState({
    classTypeId: "",
    dayOfWeek: "",
    startTime: "",
    teacherId: initialTeacherId || "",
    bookId: "",
    isRecurring: false,
    fromDate: "",
    tillDate: "",
    comment: "",
  });
  const [status, setStatus] = useState(STATUS.VIEW);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (isOpen && initialDate) {
      setFormData(prev => ({
        ...prev,
        dayOfWeek: initialDayOfWeek || prev.dayOfWeek,
        startTime: initialTime?.start || "",
        fromDate: initialDate,
        teacherId: initialTeacherId || prev.teacherId,
      }));
      setTouched({});
      setErrors({});
      setStatus(STATUS.VIEW);
    }
  }, [isOpen, initialDate, initialTime, initialTeacherId]);

  if (!isOpen) return null;

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    switch (name) {
      case "classTypeId":
        if (!value) newErrors.classTypeId = "Wybierz typ zajęć";
        break;
      case "dayOfWeek":
        if (!value) newErrors.dayOfWeek = "Wybierz dzień tygodnia";
        break;
      case "startTime":
        if (!value) newErrors.startTime = "Podaj czas rozpoczęcia";
        break;
      case "teacherId":
        if (!value) newErrors.teacherId = "Wybierz nauczyciela";
        break;
      case "tillDate":
        if (formData.isRecurring && !value) newErrors.tillDate = "Podaj datę końcową";
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.classTypeId) newErrors.classTypeId = "Wybierz typ zajęć";
    if (!formData.dayOfWeek) newErrors.dayOfWeek = "Wybierz dzień tygodnia";
    if (!formData.startTime) newErrors.startTime = "Podaj czas rozpoczęcia";
    if (!formData.teacherId) newErrors.teacherId = "Wybierz nauczyciela";
    if (formData.isRecurring && !formData.tillDate) newErrors.tillDate = "Podaj datę końcową";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setStatus(STATUS.SUBMITTING);
    try {
      await onSave(formData);
    } catch (error) {
      setErrors({ ...errors, submitError: error.message });
      setStatus(STATUS.SUBMITTED);
    }
  };

  const selectedClassType = classTypes.find(ct => ct.id === formData.classTypeId);
  const startTimeMinutes = timeStringToMinutes(formData.startTime);
  const endTime = (selectedClassType && startTimeMinutes) 
    ? minutesToTimeString(startTimeMinutes + selectedClassType.length) 
    : "";

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Nowe zajęcia</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {errors.submitError && (
                <div className="alert alert-danger">{errors.submitError}</div>
              )}
              <div className="form-row">
                <div className="col-md-4 mb-3">
                  <SelectInput
                    label="Dzień tygodnia"
                    name="dayOfWeek"
                    value={formData.dayOfWeek}
                    error={errors.dayOfWeek}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    defaultOption="Wybierz dzień tygodnia"
                    options={weekdays.map(d => ({ value: d.id, text: d.name }))}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <SelectInput
                    label="Typ zajęć"
                    name="classTypeId"
                    value={formData.classTypeId}
                    error={errors.classTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    defaultOption="Wybierz typ zajęć"
                    options={classTypes.map(ct => ({ value: ct.id, text: ct.name }))}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <Input
                    label="Początek"
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    error={errors.startTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-md-4 mb-3">
                  <Input
                    label="Koniec"
                    type="time"
                    name="endTime"
                    value={endTime}
                    readOnly
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <SelectInput
                    label="Nauczyciel"
                    name="teacherId"
                    value={formData.teacherId}
                    error={errors.teacherId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    defaultOption="Wybierz nauczyciela"
                    options={teachers.map(t => ({ value: t.id, text: `${t.firstName} ${t.lastName}` }))}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <SelectInput
                    label="Książka"
                    name="bookId"
                    value={formData.bookId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    defaultOption="Wybierz książkę (opcjonalnie)"
                    options={books.map(b => ({ value: b.id, text: b.title }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-md-4 mb-3">
                  <Checkbox
                    label="Zajęcia cykliczne"
                    name="isRecurring"
                    isChecked={formData.isRecurring}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <Input
                    type="date"
                    label={formData.isRecurring ? "Pierwszy tydzień" : "Data"}
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                {formData.isRecurring && (
                  <div className="col-md-4 mb-3">
                    <Input
                      type="date"
                      label="Ostatni tydzień"
                      name="tillDate"
                      value={formData.tillDate}
                      error={errors.tillDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="col-12 mb-3">
                  <Input
                    label="Uwagi"
                    type="textarea"
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Uwagi (opcjonalnie)"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="submit" className="btn btn-primary" disabled={status === STATUS.SUBMITTING}>
                {status === STATUS.SUBMITTING ? "Zapisywanie..." : "Zapisz"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Anuluj
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

CreateClassModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialDate: PropTypes.string,
  initialTime: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
  initialDayOfWeek: PropTypes.string,
  initialTeacherId: PropTypes.string,
  classTypes: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  books: PropTypes.array.isRequired,
  weekdays: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

CreateClassModal.defaultProps = {
  initialDate: "",
  initialTime: null,
  initialDayOfWeek: "",
  initialTeacherId: "",
};

export default CreateClassModal;
